import type {
  ParsedOutboundVideoStreamStats,
  ParsedOutboundAudioStreamStats,
} from 'webrtc-issue-detector';
import type StatsHandler from './StatsHandler';
import type { ParsedOutboundAudioStreamCompositeStats, ParsedOutboundVideoStreamCompositeStats } from './types';
import type { MOSScores } from '../../qoe/types';
import type { ScoresCalculator } from '../../qoe/ScoresCalculator';
import type { WIDStatsAdapter } from '../../qoe/WIDStatsAdapter';
import { getLocalStorageItem } from '../analyticsApiClient/helper';
import BaseTrackStatsManager from './BaseTrackStatsManager';

interface OutboundTrackStatsManagerParams {
  track: MediaStreamTrack;
  statsHandler: StatsHandler;
  widStatsAdapter: WIDStatsAdapter;
  scoresCalculator: ScoresCalculator;
  isStaticContent?: boolean;
}

export default class OutboundTrackStatsManager extends BaseTrackStatsManager {
  #videoWebRTCStatsGroup?: ParsedOutboundVideoStreamCompositeStats[];

  #audioWebRTCStats?: ParsedOutboundAudioStreamCompositeStats;

  #Qv?: number;

  #Qa?: number;

  readonly #scoresCalculator: ScoresCalculator;

  readonly #statsAdapter: WIDStatsAdapter;

  readonly #isStaticContent?: boolean;

  constructor({
    track,
    statsHandler,
    widStatsAdapter,
    scoresCalculator,
    isStaticContent,
  }: OutboundTrackStatsManagerParams) {
    super({ track });

    this.handleWebRTCStats(track, statsHandler);
    this.#statsAdapter = widStatsAdapter;
    this.#scoresCalculator = scoresCalculator;
    this.#isStaticContent = isStaticContent;
  }

  get videoWebRTCStats(): ParsedOutboundVideoStreamStats | undefined {
    return this.#videoWebRTCStatsGroup?.find((s) => s.outbound.active)?.outbound
      ?? this.#videoWebRTCStatsGroup?.[0]?.outbound;
  }

  get audioWebRTCStats(): ParsedOutboundAudioStreamStats | undefined {
    return this.#audioWebRTCStats?.outbound;
  }

  get scores(): MOSScores {
    return {
      Qv: this.#Qv,
      Qa: this.#Qa,
    };
  }

  private handleWebRTCStats(track: MediaStreamTrack, statsHandler: StatsHandler): void {
    track.addEventListener('ended', () => {
      statsHandler.removeOutboundVideoHandler(track.id);
      statsHandler.removeOutboundAudioHandler(track.id);
    }, { once: true });

    if (track.kind === 'video') {
      statsHandler.addOutboundVideoHandler(track.id, this.setVideoWebRTCStats.bind(this));

      return;
    }

    statsHandler.addOutboundAudioHandler(track.id, this.setAudioWebRTCStats.bind(this));
  }

  private setVideoWebRTCStats(statsGroup: ParsedOutboundVideoStreamCompositeStats[]): void {
    this.touchLastReceivedStatsTimestamp();
    const activeStats = statsGroup.filter((s) => s.outbound.active);
    const qvValues: number[] = [];

    activeStats.forEach((stats) => {
      const prevStats = this.#videoWebRTCStatsGroup?.find(
        (prev) => prev.outbound.ssrc === stats.outbound.ssrc,
      );
      const preparedStats = this.#statsAdapter.adaptOutboundVideoStats({
        stats: stats.outbound,
        remoteInboundStats: stats.remoteInbound,
        prevStats,
        isStaticContent: this.#isStaticContent,
      });
      qvValues.push(this.#scoresCalculator.calculateVideoMOS(preparedStats));
    });

    this.#Qv = qvValues.length > 0 ? Math.min(...qvValues) : undefined;
    this.#videoWebRTCStatsGroup = statsGroup;
  }

  private setAudioWebRTCStats(stats: ParsedOutboundAudioStreamCompositeStats): void {
    this.touchLastReceivedStatsTimestamp();
    const prevStats = this.#audioWebRTCStats
      ? { outbound: this.#audioWebRTCStats.outbound, remoteInbound: this.#audioWebRTCStats.remoteInbound }
      : undefined;
    const preparedStats = this.#statsAdapter.adaptOutboundAudioStats({
      stats: stats.outbound,
      remoteInboundStats: stats.remoteInbound,
      fec: getLocalStorageItem('qosOutboundFec') ?? true,
      dtx: getLocalStorageItem('qosOutboundDtx') ?? true,
      prevStats,
    });
    this.#Qa = this.#scoresCalculator.calculateAudioMOS(preparedStats);
    this.#audioWebRTCStats = stats;
  }

  protected cleanupStats(): void {
    this.#videoWebRTCStatsGroup = undefined;
    this.#audioWebRTCStats = undefined;
    this.#Qv = undefined;
    this.#Qa = undefined;
  }
}
